import { Button } from '@components'
import Color from 'color'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaCheck, FaPlus } from 'react-icons/fa'
import styled from 'styled-components'

const AUTOMATIONS_GUIDE_URL = ''

const BENEFIT_KEYS = ['recurring', 'nothingHanging', 'inControl'] as const

interface WorkflowsEmptyStateProps {
  onCreate: () => void
  isCreating?: boolean
}

export const WorkflowsEmptyState: React.FC<WorkflowsEmptyStateProps> = ({
  onCreate,
  isCreating = false
}) => {
  const [t] = useTranslation()

  return (
    <Root>
      <Column>
        <IntroBlock>
          <BetaPill>{t('workflows.emptyState.badge')}</BetaPill>
          <Headline>{t('workflows.emptyState.title')}</Headline>
          <Description>
            <Trans i18nKey="workflows.emptyState.description" components={{ b: <Strong /> }} />
          </Description>
        </IntroBlock>

        <BenefitList>
          {BENEFIT_KEYS.map(key => (
            <BenefitRow key={key}>
              <CheckTile>
                <FaCheck />
              </CheckTile>
              <BenefitText>
                <BenefitTitle>{t(`workflows.emptyState.benefits.${key}.title`)}</BenefitTitle>
                <BenefitDescription>
                  {t(`workflows.emptyState.benefits.${key}.description`)}
                </BenefitDescription>
              </BenefitText>
            </BenefitRow>
          ))}
        </BenefitList>

        <ActionRow>
          <PrimaryButton
            type="button"
            intent="success"
            size="lg"
            icon={<FaPlus />}
            disabled={isCreating}
            showSpinner={isCreating}
            onClick={onCreate}
          >
            {t('workflows.emptyState.create')}
          </PrimaryButton>
          {AUTOMATIONS_GUIDE_URL ? (
            <GuideButton href={AUTOMATIONS_GUIDE_URL} target="_blank" rel="noreferrer">
              {t('workflows.emptyState.guide')}
            </GuideButton>
          ) : null}
        </ActionRow>
      </Column>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xxl}rem ${({ theme }) => theme.spacing.xl}rem;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.625rem;
  max-width: 40rem;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    max-width: none;
  }
`

const IntroBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const BetaPill = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  background: ${({ theme }) => theme.luca.tint};
  color: ${({ theme }) => theme.luca.deepBlue};
  font-size: ${({ theme }) => theme.fontSize.px115}rem;
  font-weight: 700;
  letter-spacing: 0.08em;
`

const Headline = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.px31}rem;
  font-weight: 700;
  line-height: 1.18;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.menuBg};
  text-wrap: balance;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    font-size: 1.625rem;
  }
`

const Description = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.luca.textMuted};
  text-wrap: pretty;
`

const Strong = styled.strong`
  color: ${({ theme }) => theme.colors.menuBg};
  font-weight: 700;
`

const BenefitList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`

const BenefitRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`

const CheckTile = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: color-mix(in srgb, ${({ theme }) => theme.colors.success} 8%, white);
  border: 1px solid color-mix(in srgb, ${({ theme }) => theme.colors.success} 30%, white);
  color: ${({ theme }) => Color(theme.colors.success).darken(0.45).hex()};

  svg {
    width: 14px;
    height: 14px;
  }
`

const BenefitText = styled.div`
  display: flex;
  flex-direction: column;
`

const BenefitTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.px145}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBg};
`

const BenefitDescription = styled.div`
  font-size: ${({ theme }) => theme.fontSize.px135}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.luca.textMuted};
`

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`

const PrimaryButton = styled(Button)`
  box-shadow: 0 4px 14px ${({ theme }) => Color(theme.colors.success).alpha(0.35).string()};

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    width: 100%;
    justify-content: center;
  }
`

const GuideButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: 0.2s;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.neutralGray};
  }

  &:active {
    background: ${({ theme }) => theme.colors.neutralGray};
    filter: brightness(90%);
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.md}) {
    width: 100%;
  }
`
